import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { createGenericReducers, errorSideEffects, RTKSideEffectSchema, successSideEffects, updatingSideEffects } from "..";
import { V2ErrorResponseSchema } from "../../actions";
import { Toastify } from "../../../components/Toaster";

export interface PasswordlessChannelSettings {
  IsEnabled?: boolean;
  EnableAutoRegistration?: boolean;
}

export interface PasswordlessLoginSettingsData {
  Email?: PasswordlessChannelSettings;
  Phone?: PasswordlessChannelSettings;
}

// Shape of the GET/PUT /platform-configuration/passwordless-login-settings response: the settings
// document itself, or a V2 error envelope.
export interface PasswordlessLoginSettingsResponse extends PasswordlessLoginSettingsData, V2ErrorResponseSchema {}

export interface PasswordlessLoginSettingsSchema extends RTKSideEffectSchema {
  Data?: PasswordlessLoginSettingsData;
}

// Outcome toasts are raised from the reducers (the prevailing convention across redux/features), but a
// reducer can't reach state.appState.i18n. So the dispatching container passes the localized strings in
// with the request and the saga hands the resolved one to the outcome action — the same route
// updateWebHookAction takes (redux/actions/integrationsActions.tsx).
export interface PasswordlessLoginSettingsMessages {
  success: string;
  error: string;
}

export interface UpdatePasswordlessLoginSettingsRequestPayload {
  settings: PasswordlessLoginSettingsData;
  messages: PasswordlessLoginSettingsMessages;
}

export interface UpdatePasswordlessLoginSettingsSuccessPayload {
  data: PasswordlessLoginSettingsData;
  message: string;
}

const initialState: PasswordlessLoginSettingsSchema = {
  Data: {},
  isLoading: false,
  isUpdating: false,
  isSuccess: false,
  isError: false
};

const passwordlessLoginSettingsSlice = createSlice({
  name: "passwordlessLoginSettings",
  initialState,
  reducers: {
    // The request carries the i18n fallback used if the load fails without a server-supplied message.
    getPasswordlessLoginSettingsRequest: createGenericReducers<PasswordlessLoginSettingsSchema, PasswordlessLoginSettingsMessages["error"]>({}).request,
    getPasswordlessLoginSettingsSuccess: createGenericReducers<PasswordlessLoginSettingsSchema, PasswordlessLoginSettingsData>({}).success,
    // A failed LOAD is recorded but deliberately NOT toasted: the admin didn't ask for anything, so a
    // popup on entering the screen is noise. The shared `.failure` reducer toasts, so this one is
    // hand-written. Saves still toast — see updatePasswordlessLoginSettingsFailure below.
    getPasswordlessLoginSettingsFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.error = action.payload;
    },
    updatePasswordlessLoginSettingsRequest(state, _action: PayloadAction<UpdatePasswordlessLoginSettingsRequestPayload>) {
      Object.assign(state, updatingSideEffects);
    },
    updatePasswordlessLoginSettingsSuccess(state, action: PayloadAction<UpdatePasswordlessLoginSettingsSuccessPayload>) {
      Object.assign(state, successSideEffects);
      state.Data = action.payload.data;
      // A previous failure must not survive a successful save (successSideEffects carries no `error`).
      state.error = "";
      Toastify("success", action.payload.message);
    },
    updatePasswordlessLoginSettingsFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.error = action.payload;
      Toastify("error", action.payload);
    },
    // Clears channel state on app/tenant switch so the toggles never render a previous app's values.
    resetPasswordlessLoginSettings() {
      return initialState;
    }
  }
});

export const {
  getPasswordlessLoginSettingsRequest,
  getPasswordlessLoginSettingsSuccess,
  getPasswordlessLoginSettingsFailure,
  updatePasswordlessLoginSettingsRequest,
  updatePasswordlessLoginSettingsSuccess,
  updatePasswordlessLoginSettingsFailure,
  resetPasswordlessLoginSettings
} = passwordlessLoginSettingsSlice.actions;

export default passwordlessLoginSettingsSlice.reducer;
